import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Users, Shield, LogOut, AlertTriangle, MessageCircle, MessageCircleOff } from 'lucide-react'
import { useTelegramLink } from '@/hooks/useTelegramLink'

interface GroupSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  onGroupLeft?: () => void
  onGroupRenamed?: () => void
}

interface MemberPermissions {
  canManagePersons: boolean
  canManageClassifications: boolean
}

interface GroupDetails {
  id: string
  name: string
  memberCount: number
  admins: string[]
  isAdmin: boolean
  alertsEnabled: boolean
  alertClassificationIds?: string[]
  permissions: MemberPermissions
  classificationIds?: string[]
  createdAt: any
  createdBy: string
}

interface ClassificationInfo {
  id: string
  displayName: string
  color: string
  alertEnabled: boolean
}

export function GroupSettingsDialog({ open, onOpenChange, groupId, onGroupLeft, onGroupRenamed }: GroupSettingsDialogProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [groupDetails, setGroupDetails] = useState<GroupDetails | null>(null)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [updatingAlerts, setUpdatingAlerts] = useState(false)
  const [classifications, setClassifications] = useState<ClassificationInfo[]>([])
  const [alertClassificationIds, setAlertClassificationIds] = useState<string[] | undefined>(undefined)
  const [savingAlertClassifications, setSavingAlertClassifications] = useState(false)
  const { linked: telegramLinked } = useTelegramLink()
  const [newName, setNewName] = useState('')
  const [savingName, setSavingName] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (open && groupId) {
      loadGroupDetails()
      setShowLeaveConfirm(false)
    }
  }, [open, groupId])

  const loadGroupDetails = async () => {
    try {
      setLoading(true)
      setError('')
      const [groupResult, classResult] = await Promise.all([
        httpsCallable(functions, 'getGroupDetails')({ groupId }),
        httpsCallable(functions, 'getClassifications')({ groupId }),
      ])
      const data = groupResult.data as { success: boolean; group: GroupDetails }
      const classData = classResult.data as { success: boolean; classifications: ClassificationInfo[] }

      if (data.success) {
        setGroupDetails(data.group)
        setNewName(data.group.name)
        setAlertClassificationIds(data.group.alertClassificationIds)
      } else {
        setError(t('groupSettings.failedToLoad'))
      }
      if (classData.success) {
        setClassifications(classData.classifications)
      }
    } catch (err) {
      console.error('Load group details error:', err)
      setError(t('groupSettings.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }

  const handleLeaveGroup = async () => {
    try {
      setLeaving(true)
      const leaveGroup = httpsCallable(functions, 'leaveGroup')
      await leaveGroup({ groupId })
      handleClose()
      onGroupLeft?.()
    } catch (err: any) {
      console.error('Leave group error:', err)
      if (err.code === 'functions/failed-precondition') {
        setError(t('groupSettings.adminsCannotLeave'))
      } else {
        setError(t('groupSettings.failedToLeave'))
      }
      setShowLeaveConfirm(false)
    } finally {
      setLeaving(false)
    }
  }

  const handleAlertsToggle = async (enabled: boolean) => {
    try {
      setUpdatingAlerts(true)
      setError('')
      const updateMemberAlertPreference = httpsCallable(functions, 'updateMemberAlertPreference')
      await updateMemberAlertPreference({ groupId, alertsEnabled: enabled })

      // Update local state
      if (groupDetails) {
        setGroupDetails({ ...groupDetails, alertsEnabled: enabled })
      }
    } catch (err: any) {
      console.error('Update alerts error:', err)
      setError(t('groupSettings.failedToUpdateAlerts'))
    } finally {
      setUpdatingAlerts(false)
    }
  }

  const handleAlertClassificationToggle = async (classificationId: string, checked: boolean) => {
    if (!groupDetails) return
    try {
      setSavingAlertClassifications(true)
      setError('')

      // Compute eligible classification IDs
      const eligibleIds = getEligibleClassificationIds()

      let newIds: string[]
      if (alertClassificationIds == null) {
        // Legacy "all" state — materialize all eligible minus the unchecked one
        newIds = checked ? eligibleIds : eligibleIds.filter(id => id !== classificationId)
      } else {
        newIds = checked
          ? [...alertClassificationIds, classificationId]
          : alertClassificationIds.filter(id => id !== classificationId)
      }

      // If all eligible are checked, revert to undefined (all)
      const finalIds = eligibleIds.length > 0 && eligibleIds.every(id => newIds.includes(id))
        ? undefined
        : newIds

      const updateFn = httpsCallable(functions, 'updateMemberAlertPreference')
      await updateFn({ groupId, alertClassificationIds: finalIds ?? eligibleIds })

      setAlertClassificationIds(finalIds)
      if (groupDetails) {
        setGroupDetails({ ...groupDetails, alertClassificationIds: finalIds })
      }
    } catch (err) {
      console.error('Update alert classifications error:', err)
      setError(t('groupSettings.failedToUpdateAlertClassifications'))
    } finally {
      setSavingAlertClassifications(false)
    }
  }

  const getEligibleClassificationIds = (): string[] => {
    if (!groupDetails) return []
    const isAdmin = groupDetails.isAdmin
    const canManageClassifications = groupDetails.permissions?.canManageClassifications

    return classifications
      .filter(c => {
        if (!c.alertEnabled) return false
        if (isAdmin || canManageClassifications) return true
        return (groupDetails.classificationIds || []).includes(c.id)
      })
      .map(c => c.id)
  }

  const handleRenameSave = async () => {
    const trimmed = newName.trim()
    if (!trimmed || trimmed === groupDetails?.name) {
      return
    }
    try {
      setSavingName(true)
      setError('')
      const renameGroupFn = httpsCallable(functions, 'renameGroup')
      await renameGroupFn({ groupId, name: trimmed })
      if (groupDetails) {
        setGroupDetails({ ...groupDetails, name: trimmed })
      }
      onGroupRenamed?.()
    } catch (err) {
      console.error('Rename group error:', err)
      setError(t('groupSettings.failedToRename'))
    } finally {
      setSavingName(false)
    }
  }

  const handleClose = () => {
    setGroupDetails(null)
    setClassifications([])
    setAlertClassificationIds(undefined)
    setError('')
    setShowLeaveConfirm(false)
    onOpenChange(false)
  }

  // Render leave confirmation view
  if (showLeaveConfirm) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('groupSettings.leaveTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('groupSettings.leaveConfirm', { name: groupDetails?.name })}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowLeaveConfirm(false)}
              disabled={leaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleLeaveGroup}
              disabled={leaving}
            >
              {leaving ? t('groupSettings.leaving') : t('groupSettings.leaveGroup')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('groupSettings.title')}</DialogTitle>
          <DialogDescription>
            {t('groupSettings.description')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : groupDetails ? (
            /* Full member/admin view */
            <div className="space-y-4">
              {/* Group section */}
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('groupSettings.groupSection')}</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>{t('groupSettings.groupName')}</Label>
                  {groupDetails.isAdmin && (
                    <Badge variant="outline" className="text-xs">
                      <Shield className="h-3 w-3 mr-1" />
                      {t('groupSettings.admin')}
                    </Badge>
                  )}
                </div>
                {groupDetails.isAdmin ? (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      maxLength={100}
                      disabled={savingName}
                    />
                    {newName.trim() !== groupDetails.name && (
                      <Button
                        size="sm"
                        onClick={handleRenameSave}
                        disabled={savingName || !newName.trim()}
                      >
                        {savingName ? t('common.saving') : t('common.save')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <Input
                    type="text"
                    value={groupDetails.name}
                    readOnly
                    className="bg-paper-2/40"
                  />
                )}
              </div>

              <div className="flex items-center justify-between p-3 bg-paper-2/40 rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {t('groupSettings.members', { count: groupDetails.memberCount })}
                  </span>
                </div>
                {groupDetails.isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleClose()
                      window.location.href = `/members?groupId=${groupId}`
                    }}
                  >
                    <Users className="h-4 w-4 mr-1" />
                    {t('common.manage')}
                  </Button>
                )}
              </div>

              {/* My Preferences section */}
              <div className="pt-2 border-t space-y-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('groupSettings.myPreferencesSection')}</h3>
                <div className="space-y-2">
                  <Label>{t('groupSettings.telegramAlerts')}</Label>
                  <div className="flex items-center justify-between p-3 bg-paper-2/40 rounded-lg">
                    <div className="flex items-center gap-2">
                      {groupDetails.alertsEnabled ? (
                        <MessageCircle className="h-4 w-4 text-primary" />
                      ) : (
                        <MessageCircleOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {groupDetails.alertsEnabled ? t('groupSettings.alertsEnabled') : t('groupSettings.alertsDisabled')}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t('groupSettings.alertsDescription')}
                        </span>
                      </div>
                    </div>
                    <Switch
                      checked={groupDetails.alertsEnabled}
                      onCheckedChange={handleAlertsToggle}
                      disabled={updatingAlerts}
                    />
                  </div>
                  {groupDetails.alertsEnabled && (() => {
                    const eligibleClassifications = classifications.filter(c => {
                      if (!c.alertEnabled) return false
                      if (groupDetails.isAdmin || groupDetails.permissions?.canManageClassifications) return true
                      return (groupDetails.classificationIds || []).includes(c.id)
                    })
                    if (eligibleClassifications.length === 0) return null
                    return (
                      <div className="mt-3 space-y-2">
                        <Label className="text-xs text-muted-foreground">{t('groupSettings.alertClassifications')}</Label>
                        <div className="space-y-3">
                          {eligibleClassifications.map(c => {
                            const isChecked = alertClassificationIds == null || alertClassificationIds.includes(c.id)
                            return (
                              <div key={c.id} className="flex items-center justify-between">
                                <span className="text-sm flex items-center gap-2">
                                  <span
                                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: c.color }}
                                  />
                                  {c.displayName}
                                </span>
                                <Switch
                                  checked={isChecked}
                                  onCheckedChange={(checked) => handleAlertClassificationToggle(c.id, checked)}
                                  disabled={savingAlertClassifications}
                                />
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })()}
                  {!telegramLinked && (
                    <p className="text-xs text-amber-600">
                      {t('groupSettings.linkTelegramHint')}
                    </p>
                  )}
                </div>

              {/* Leave group button (only for non-admins) */}
              {!groupDetails.isAdmin && (
                <div className="pt-2">
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => setShowLeaveConfirm(true)}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('groupSettings.leaveGroup')}
                  </Button>
                </div>
              )}

              {groupDetails.isAdmin && (
                <div className="pt-2">
                  <p className="text-xs text-muted-foreground text-center">
                    {t('groupSettings.adminCannotLeave')}
                  </p>
                </div>
              )}
              </div>
            </div>
        ) : null}

        <div className="flex justify-end">
          <Button onClick={handleClose}>{t('common.close')}</Button>
        </div>
      </DialogContent>

    </Dialog>
  )
}
